import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import styles from './LiveLearning.module.css';

const LiveLearning = ({ onBack }) => {
  const { t } = useTranslation('liveLearning');
  const [activeTab, setActiveTab] = useState('overview');
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState({});

  const tabs = [
    { id: 'overview', label: t('tabs.overview'), icon: 'dashboard' },
    { id: 'teachers', label: t('tabs.teachers'), icon: 'school' },
    { id: 'students', label: t('tabs.students'), icon: 'people' },
    { id: 'bookings', label: t('tabs.bookings'), icon: 'event' },
    { id: 'payments', label: t('tabs.payments'), icon: 'payment' },
    { id: 'feedback', label: t('tabs.feedback'), icon: 'rate_review' },
    { id: 'analytics', label: t('tabs.analytics'), icon: 'analytics' },
    { id: 'settings', label: t('tabs.settings'), icon: 'settings' }
  ];

  useEffect(() => {
    loadSampleData();
  }, []);

  const loadSampleData = () => {
    // Sample data for teachers
    setTeachers([
      {
        id: 1,
        name: 'Dr. Sarah Johnson',
        email: 'sarah.johnson@example.com',
        subject: 'English',
        rate: 45,
        rating: 4.8,
        status: 'active',
        bookings: 156,
        earnings: 7020
      },
      {
        id: 2,
        name: 'Prof. Mike Chen',
        email: 'mike.chen@example.com',
        subject: 'Mathematics',
        rate: 50,
        rating: 4.9,
        status: 'active',
        bookings: 203,
        earnings: 10150
      },
      {
        id: 3,
        name: 'Maria Garcia',
        email: 'maria.garcia@example.com',
        subject: 'Spanish',
        rate: 40,
        rating: 4.7,
        status: 'pending',
        bookings: 89,
        earnings: 3560
      }
    ]);

    // Sample data for students
    setStudents([
      {
        id: 1,
        name: 'John Smith',
        email: 'john.smith@example.com',
        bookings: 12,
        totalSpent: 540,
        lastActivity: '2024-01-15',
        status: 'active'
      },
      {
        id: 2,
        name: 'Emma Wilson',
        email: 'emma.wilson@example.com',
        bookings: 8,
        totalSpent: 360,
        lastActivity: '2024-01-14',
        status: 'active'
      },
      {
        id: 3,
        name: 'David Brown',
        email: 'david.brown@example.com',
        bookings: 5,
        totalSpent: 225,
        lastActivity: '2024-01-10',
        status: 'inactive'
      }
    ]);

    // Sample data for bookings
    setBookings([
      {
        id: 1,
        student: 'John Smith',
        teacher: 'Dr. Sarah Johnson',
        subject: 'English',
        dateTime: '2024-01-16T10:00:00',
        duration: 60,
        amount: 45,
        status: 'upcoming'
      },
      {
        id: 2,
        student: 'Emma Wilson',
        teacher: 'Prof. Mike Chen',
        subject: 'Mathematics',
        dateTime: '2024-01-16T14:00:00',
        duration: 90,
        amount: 75,
        status: 'completed'
      },
      {
        id: 3,
        student: 'David Brown',
        teacher: 'Maria Garcia',
        subject: 'Spanish',
        dateTime: '2024-01-15T16:00:00',
        duration: 60,
        amount: 40,
        status: 'cancelled'
      }
    ]);

    // Sample data for payments
    setPayments([
      {
        id: 1,
        date: '2024-01-15',
        student: 'John Smith',
        teacher: 'Dr. Sarah Johnson',
        subject: 'English',
        amount: 45,
        status: 'completed'
      },
      {
        id: 2,
        date: '2024-01-14',
        student: 'Emma Wilson',
        teacher: 'Prof. Mike Chen',
        subject: 'Mathematics',
        amount: 75,
        status: 'completed'
      },
      {
        id: 3,
        date: '2024-01-13',
        student: 'David Brown',
        teacher: 'Maria Garcia',
        subject: 'Spanish',
        amount: 40,
        status: 'pending'
      }
    ]);

    // Sample data for reviews
    setReviews([
      {
        id: 1,
        student: 'John Smith',
        teacher: 'Dr. Sarah Johnson',
        rating: 5,
        comment: 'Excellent teaching method. Very patient and clear explanations.',
        date: '2024-01-15',
        subject: 'English'
      },
      {
        id: 2,
        student: 'Emma Wilson',
        teacher: 'Prof. Mike Chen',
        rating: 4,
        comment: 'Great teacher, helped me understand complex math concepts.',
        date: '2024-01-14',
        subject: 'Mathematics'
      },
      {
        id: 3,
        student: 'David Brown',
        teacher: 'Maria Garcia',
        rating: 5,
        comment: 'Amazing Spanish lessons. Very interactive and fun!',
        date: '2024-01-13',
        subject: 'Spanish'
      }
    ]);

    // Sample analytics data
    setAnalytics({
      activeTeachers: 24,
      totalStudents: 1247,
      todayBookings: 67,
      weeklyRevenue: 12450,
      averageRating: 4.8,
      responseRate: 98,
      complaints: 12
    });
  };

  const renderOverview = () => (
    <div>
      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">school</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.activeTeachers')}</h3>
            <p className={styles.statNumber}>{analytics.activeTeachers}</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+3 {t('stats.thisWeek')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">people</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.totalStudents')}</h3>
            <p className={styles.statNumber}>{analytics.totalStudents}</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+45 {t('stats.thisWeek')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">event</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.todayBookings')}</h3>
            <p className={styles.statNumber}>{analytics.todayBookings}</p>
            <span className={`${styles.statChange} ${styles.neutral}`}>5 {t('stats.completed')}</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <span className="material-icons">trending_up</span>
          </div>
          <div className={styles.statContent}>
            <h3>{t('stats.weeklyRevenue')}</h3>
            <p className={styles.statNumber}>${analytics.weeklyRevenue}</p>
            <span className={`${styles.statChange} ${styles.positive}`}>+18% {t('stats.vsLastWeek')}</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h2>{t('quickActions.title')}</h2>
        <div className={styles.actionButtons}>
          <button className={styles.actionBtn} onClick={() => setActiveTab('teachers')}>
            <span className="material-icons">person_add</span>
            <span>{t('quickActions.addNewTeacher')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('bookings')}>
            <span className="material-icons">event_available</span>
            <span>{t('quickActions.viewTodayBookings')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('feedback')}>
            <span className="material-icons">warning</span>
            <span>{t('quickActions.reviewComplaints')}</span>
          </button>
          <button className={styles.actionBtn} onClick={() => setActiveTab('analytics')}>
            <span className="material-icons">assessment</span>
            <span>{t('quickActions.viewReports')}</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className={styles.recentActivity}>
        <h2>{t('recentActivity.title')}</h2>
        <div className={styles.activityList}>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">event</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('recentActivity.newBooking', { student: 'Sarah Johnson', teacher: 'Teacher Mike' })}</strong></p>
              <span className={styles.activityTime}>{t('recentActivity.minutesAgo', { count: 2 })}</span>
            </div>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>{t('recentActivity.view')}</button>
          </div>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">school</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('recentActivity.teacherApproved', { name: 'Maria Garcia' })}</strong></p>
              <span className={styles.activityTime}>{t('recentActivity.minutesAgo', { count: 15 })}</span>
            </div>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>{t('recentActivity.view')}</button>
          </div>
          <div className={styles.activityItem}>
            <div className={styles.activityIcon}>
              <span className="material-icons">payment</span>
            </div>
            <div className={styles.activityContent}>
              <p><strong>{t('recentActivity.paymentReceived', { amount: 150, student: 'John Smith', course: 'IELTS prep' })}</strong></p>
              <span className={styles.activityTime}>{t('recentActivity.hourAgo', { count: 1 })}</span>
            </div>
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>{t('recentActivity.view')}</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTeachers = () => (
    <div>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder={t('filters.searchTeachers')} />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">{t('filters.allStatus')}</option>
            <option value="active">{t('filters.active')}</option>
            <option value="pending">{t('filters.pending')}</option>
            <option value="suspended">{t('filters.suspended')}</option>
          </select>
          <select>
            <option value="">{t('filters.allSubjects')}</option>
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
          </select>
        </div>
      </div>

      <div className={styles.teachersGrid}>
        {teachers.map(teacher => (
          <div key={teacher.id} className={styles.teacherCard}>
            <div className={styles.teacherHeader}>
              <h3>{teacher.name}</h3>
              <span className={`${styles.statusBadge} ${styles[`status${teacher.status.charAt(0).toUpperCase() + teacher.status.slice(1)}`]}`}>
                {teacher.status}
              </span>
            </div>
            <div className={styles.teacherInfo}>
              <p><strong>Email:</strong> {teacher.email}</p>
              <p><strong>Subject:</strong> {teacher.subject}</p>
              <p><strong>Rate:</strong> ${teacher.rate}/hour</p>
              <p><strong>Rating:</strong> {teacher.rating}/5.0</p>
            </div>
            <div className={styles.teacherActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                <span className="material-icons">edit</span> Edit
              </button>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                <span className="material-icons">{teacher.status === 'active' ? 'block' : 'check_circle'}</span>
                {teacher.status === 'active' ? 'Suspend' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStudents = () => (
    <div>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search students..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className={styles.studentsTableContainer}>
        <table className={styles.studentsTable}>
          <thead>
            <tr>
              <th>Student ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Bookings</th>
              <th>Total Spent</th>
              <th>Last Activity</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map(student => (
              <tr key={student.id}>
                <td>{student.id}</td>
                <td>{student.name}</td>
                <td>{student.email}</td>
                <td>{student.bookings}</td>
                <td>${student.totalSpent}</td>
                <td>{new Date(student.lastActivity).toLocaleDateString()}</td>
                <td>
                  <span className={`${styles.statusBadge} ${styles[`status${student.status.charAt(0).toUpperCase() + student.status.slice(1)}`]}`}>
                    {student.status}
                  </span>
                </td>
                <td>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                    <span className="material-icons">visibility</span>
                  </button>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                    <span className="material-icons">edit</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderBookings = () => (
    <div>
      <div className={styles.filtersBar}>
        <div className={styles.searchBox}>
          <span className="material-icons">search</span>
          <input type="text" placeholder="Search bookings..." />
        </div>
        <div className={styles.filterGroup}>
          <select>
            <option value="">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no-show">No Show</option>
          </select>
        </div>
      </div>

      <div className={styles.bookingsTableContainer}>
        <table className={styles.bookingsTable}>
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Student</th>
              <th>Teacher</th>
              <th>Subject</th>
              <th>Date & Time</th>
              <th>Duration</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map(booking => (
              <tr key={booking.id}>
                <td>{booking.id}</td>
                <td>{booking.student}</td>
                <td>{booking.teacher}</td>
                <td>{booking.subject}</td>
                <td>{new Date(booking.dateTime).toLocaleString()}</td>
                <td>{booking.duration} min</td>
                <td>${booking.amount}</td>
                <td>
                  <span className={`${styles.statusBadge} ${styles[`status${booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}`]}`}>
                    {booking.status}
                  </span>
                </td>
                <td>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                    <span className="material-icons">visibility</span>
                  </button>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                    <span className="material-icons">edit</span>
                  </button>
                  <button className={`${styles.btn} ${styles.btnSm} ${styles.btnSecondary}`}>
                    <span className="material-icons">cancel</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPayments = () => (
    <div>
      <div className={styles.revenueOverview}>
        <div className={styles.revenueCard}>
          <h3>Total Revenue</h3>
          <p className={styles.revenueAmount}>$234,567</p>
          <span className={styles.revenuePeriod}>This Month</span>
        </div>
        <div className={styles.revenueCard}>
          <h3>Pending Payouts</h3>
          <p className={styles.revenueAmount}>$45,678</p>
          <span className={styles.revenuePeriod}>To Teachers</span>
        </div>
        <div className={styles.revenueCard}>
          <h3>Platform Fee</h3>
          <p className={styles.revenueAmount}>$12,345</p>
          <span className={styles.revenuePeriod}>This Month</span>
        </div>
        <div className={styles.revenueCard}>
          <h3>Completed Transactions</h3>
          <p className={styles.revenueAmount}>1,234</p>
          <span className={styles.revenuePeriod}>This Month</span>
        </div>
      </div>

      <div className={styles.transactionsSection}>
        <h2>Recent Transactions</h2>
        <div className={styles.transactionsTableContainer}>
          <table className={styles.transactionsTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                <th>Teacher</th>
                <th>Subject</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id}>
                  <td>{new Date(payment.date).toLocaleDateString()}</td>
                  <td>{payment.student}</td>
                  <td>{payment.teacher}</td>
                  <td>{payment.subject}</td>
                  <td>${payment.amount}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[`status${payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}`]}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td>
                    <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                      <span className="material-icons">visibility</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderFeedback = () => (
    <div>
      <div className={styles.feedbackStats}>
        <div className={styles.feedbackStatCard}>
          <h3>Average Rating</h3>
          <div className={styles.feedbackRating}>{analytics.averageRating}</div>
          <div className={styles.feedbackCount}>Based on 1,247 reviews</div>
        </div>
        <div className={styles.feedbackStatCard}>
          <h3>Response Rate</h3>
          <div className={styles.feedbackRating}>{analytics.responseRate}%</div>
          <div className={styles.feedbackCount}>Teacher response rate</div>
        </div>
        <div className={styles.feedbackStatCard}>
          <h3>Complaints</h3>
          <div className={styles.feedbackRating}>{analytics.complaints}</div>
          <div className={styles.feedbackCount}>This month</div>
        </div>
      </div>

      <div className={styles.reviewsList}>
        {reviews.map(review => (
          <div key={review.id} className={styles.reviewCard}>
            <div className={styles.reviewHeader}>
              <div className={styles.reviewInfo}>
                <strong>{review.student} → {review.teacher}</strong>
                <div className={styles.reviewRating}>
                  {'★'.repeat(review.rating)}{'☆'.repeat(5-review.rating)}
                </div>
              </div>
              <span className={styles.reviewDate}>{new Date(review.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.reviewContent}>
              <p>{review.comment}</p>
              <small>Subject: {review.subject}</small>
            </div>
            <div className={styles.reviewActions}>
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}>
                <span className="material-icons">visibility</span> View
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div>
      <div className={styles.analyticsGrid}>
        <div className={styles.chartContainer}>
          <h3>Booking Trends</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">show_chart</span>
            <p>Booking trends chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <h3>Revenue Analysis</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">bar_chart</span>
            <p>Revenue analysis chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <h3>Teacher Performance</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">pie_chart</span>
            <p>Teacher performance chart will be displayed here</p>
          </div>
        </div>
        <div className={styles.chartContainer}>
          <h3>Student Engagement</h3>
          <div className={styles.chartPlaceholder}>
            <span className="material-icons">trending_up</span>
            <p>Student engagement chart will be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div>
      <div className={styles.settingsGrid}>
        <div className={styles.settingsCard}>
          <h3>Booking Settings</h3>
          <div className={styles.settingItem}>
            <label htmlFor="minBookingTime">Minimum Booking Time (hours)</label>
            <input type="number" id="minBookingTime" defaultValue="2" min="1" max="24" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="maxBookingTime">Maximum Booking Time (hours)</label>
            <input type="number" id="maxBookingTime" defaultValue="8" min="1" max="24" />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Payment Settings</h3>
          <div className={styles.settingItem}>
            <label htmlFor="platformFee">Platform Fee (%)</label>
            <input type="number" id="platformFee" defaultValue="15" min="0" max="50" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="minPayoutAmount">Minimum Payout Amount ($)</label>
            <input type="number" id="minPayoutAmount" defaultValue="50" min="0" />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Quality Control</h3>
          <div className={styles.settingItem}>
            <label htmlFor="autoApproveTeachers">Auto-approve teachers</label>
            <input type="checkbox" id="autoApproveTeachers" />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="requireVideoCall">Require video call verification</label>
            <input type="checkbox" id="requireVideoCall" defaultChecked />
          </div>
        </div>

        <div className={styles.settingsCard}>
          <h3>Notifications</h3>
          <div className={styles.settingItem}>
            <label htmlFor="emailBookings">Email notifications for bookings</label>
            <input type="checkbox" id="emailBookings" defaultChecked />
          </div>
          <div className={styles.settingItem}>
            <label htmlFor="emailPayments">Email notifications for payments</label>
            <input type="checkbox" id="emailPayments" defaultChecked />
          </div>
        </div>
      </div>

      <div className={styles.settingsActions}>
        <button className={`${styles.btn} ${styles.btnPrimary}`}>Save Settings</button>
        <button className={`${styles.btn} ${styles.btnSecondary}`}>Reset to Default</button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'teachers': return renderTeachers();
      case 'students': return renderStudents();
      case 'bookings': return renderBookings();
      case 'payments': return renderPayments();
      case 'feedback': return renderFeedback();
      case 'analytics': return renderAnalytics();
      case 'settings': return renderSettings();
      default: return renderOverview();
    }
  };

  return (
    <div className={styles.liveLearningWrapper}>
      <div className={styles.liveLearningContent}>
        {onBack && (
          <button className={styles.backToCourseManagement} onClick={onBack}>
            <span className="material-icons">arrow_back</span>
            {t('backToCourseManagement')}
          </button>
        )}

        <div className={styles.tabsContainer}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className={styles.contentArea}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default LiveLearning; 